// src/components/infografia/properties/BackgroundProperties.jsx
import React, { useState, useRef } from 'react';
import { FaImage, FaPalette, FaTrash } from 'react-icons/fa';
import { MdGradient } from 'react-icons/md';
import infografiaService from '../../../helpers/infografia/infografiaService';
import Swal from 'sweetalert2';

const BackgroundProperties = ({ fondo, onUpdate, marcaId }) => {
    const [activeTab, setActiveTab] = useState(fondo?.tipo || 'color');
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    // Color sólido
    const currentColor = fondo?.tipo === 'color'
        ? (typeof fondo.valor === 'string' ? fondo.valor : fondo.valor?.color || '#FFFFFF')
        : '#FFFFFF';

    const handleColorChange = (color) => {
        onUpdate({
            tipo: 'color',
            valor: { color }
        });
    };

    // Gradiente
    const gradientData = fondo?.tipo === 'gradiente'
        ? fondo.valor
        : { direccion: '135deg', colores: [{ color: '#3B82F6', posicion: 0 }, { color: '#1D4ED8', posicion: 100 }] };

    const handleGradientChange = (field, value) => {
        onUpdate({
            tipo: 'gradiente',
            valor: {
                ...gradientData,
                [field]: value
            }
        });
    };

    const handleGradientColorChange = (index, color) => {
        const newColores = [...gradientData.colores];
        newColores[index] = { ...newColores[index], color };
        handleGradientChange('colores', newColores);
    };

    const handleGradientPositionChange = (index, posicion) => {
        const newColores = [...gradientData.colores];
        newColores[index] = { ...newColores[index], posicion: parseInt(posicion) };
        handleGradientChange('colores', newColores);
    };

    const addGradientColor = () => {
        const newColores = [...gradientData.colores, { color: '#FFFFFF', posicion: 50 }];
        handleGradientChange('colores', newColores);
    };

    const removeGradientColor = (index) => {
        if (gradientData.colores.length <= 2) {
            Swal.fire({
                title: 'Error',
                text: 'El gradiente debe tener al menos 2 colores',
                icon: 'error'
            });
            return;
        }
        const newColores = gradientData.colores.filter((_, i) => i !== index);
        handleGradientChange('colores', newColores);
    };

    // Imagen
    const imageData = fondo?.tipo === 'imagen'
        ? fondo.valor
        : { url: '', cdnUrl: '', key: '', opacidad: 1, modo: 'cover' };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            Swal.fire({
                title: 'Error',
                text: 'Por favor, selecciona un archivo de imagen válido',
                icon: 'error'
            });
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            Swal.fire({
                title: 'Error',
                text: 'La imagen es demasiado grande. Máximo 10MB',
                icon: 'error'
            });
            return;
        }

        try {
            setUploading(true);

            const formData = new FormData();
            formData.append('imagen', file);
            formData.append('marcaId', marcaId);

            const response = await infografiaService.uploadImage(formData);
            const imageData = response?.imagen || response;

            onUpdate({
                tipo: 'imagen',
                valor: {
                    url: imageData.url,
                    cdnUrl: imageData.cdnUrl || imageData.url,
                    key: imageData.key,
                    opacidad: 1,
                    modo: 'cover'
                }
            });

            Swal.fire({
                title: '¡Imagen subida!',
                text: 'La imagen de fondo se ha subido correctamente',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Error subiendo imagen:', error);
            Swal.fire({
                title: 'Error',
                text: error.message || 'No se pudo subir la imagen',
                icon: 'error'
            });
        } finally {
            setUploading(false);
        }
    };

    const handleImageRemove = () => {
        onUpdate({
            tipo: 'color',
            valor: { color: '#FFFFFF' }
        });
        setActiveTab('color');
    };

    const handleImagePropertyChange = (field, value) => {
        onUpdate({
            tipo: 'imagen',
            valor: {
                ...imageData,
                [field]: value
            }
        });
    };

    return (
        <div className="space-y-4">
            {/* Tabs */}
            <div className="flex border-b border-gray-200">
                <button
                    onClick={() => setActiveTab('color')}
                    className={`flex-1 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'color'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                >
                    <FaPalette className="inline mr-1" />
                    Color
                </button>
                <button
                    onClick={() => setActiveTab('gradiente')}
                    className={`flex-1 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'gradiente'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                >
                    <MdGradient className="inline mr-1" />
                    Gradiente
                </button>
                <button
                    onClick={() => setActiveTab('imagen')}
                    className={`flex-1 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'imagen'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                >
                    <FaImage className="inline mr-1" />
                    Imagen
                </button>
            </div>

            {/* Color Tab */}
            {activeTab === 'color' && (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Color de Fondo
                        </label>
                        <div className="flex items-center gap-3">
                            <input
                                type="color"
                                value={currentColor}
                                onChange={(e) => handleColorChange(e.target.value)}
                                className="w-16 h-16 border-2 border-gray-300 rounded-lg cursor-pointer"
                            />
                            <div className="flex-1">
                                <input
                                    type="text"
                                    value={currentColor}
                                    onChange={(e) => {
                                        if (/^#[0-9A-Fa-f]{0,6}$/.test(e.target.value)) {
                                            handleColorChange(e.target.value);
                                        }
                                    }}
                                    placeholder="#FFFFFF"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                        <div className="mt-2">
                            <div
                                className="w-full h-12 rounded-md border-2 border-gray-300"
                                style={{ backgroundColor: currentColor }}
                            />
                        </div>
                    </div>

                    {/* Colores rápidos */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Colores Rápidos
                        </label>
                        <div className="grid grid-cols-8 gap-2">
                            {[
                                '#FFFFFF', '#000000', '#F3F4F6', '#E5E7EB',
                                '#EF4444', '#F59E0B', '#10B981', '#3B82F6',
                                '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16',
                                '#F97316', '#6366F1', '#14B8A6', '#FBBF24'
                            ].map((color) => (
                                <button
                                    key={color}
                                    onClick={() => handleColorChange(color)}
                                    className={`w-8 h-8 rounded border-2 ${currentColor.toUpperCase() === color.toUpperCase()
                                        ? 'border-blue-500 ring-2 ring-blue-200'
                                        : 'border-gray-300 hover:border-gray-400'
                                        }`}
                                    style={{ backgroundColor: color }}
                                    title={color}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Gradiente Tab */}
            {activeTab === 'gradiente' && (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Dirección del Gradiente
                        </label>
                        <select
                            value={gradientData.direccion || '135deg'}
                            onChange={(e) => handleGradientChange('direccion', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="0deg">→ Horizontal</option>
                            <option value="90deg">↓ Vertical</option>
                            <option value="135deg">↘ Diagonal</option>
                            <option value="45deg">↗ Diagonal</option>
                            <option value="180deg">← Horizontal</option>
                            <option value="270deg">↑ Vertical</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Colores del Gradiente
                        </label>
                        <div className="space-y-3">
                            {gradientData.colores.map((colorStop, index) => (
                                <div key={index} className="flex items-center gap-2">
                                    <input
                                        type="color"
                                        value={colorStop.color}
                                        onChange={(e) => handleGradientColorChange(index, e.target.value)}
                                        className="w-12 h-12 border-2 border-gray-300 rounded cursor-pointer"
                                    />
                                    <input
                                        type="text"
                                        value={colorStop.color}
                                        onChange={(e) => {
                                            if (/^#[0-9A-Fa-f]{0,6}$/.test(e.target.value)) {
                                                handleGradientColorChange(index, e.target.value);
                                            }
                                        }}
                                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                    />
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={colorStop.posicion}
                                        onChange={(e) => handleGradientPositionChange(index, e.target.value)}
                                        className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                                    />
                                    <span className="text-sm text-gray-500">%</span>
                                    {gradientData.colores.length > 2 && (
                                        <button
                                            onClick={() => removeGradientColor(index)}
                                            className="p-1 text-red-500 hover:text-red-700"
                                        >
                                            <FaTrash className="text-sm" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        <button
                            onClick={addGradientColor}
                            className="mt-2 w-full px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                        >
                            + Añadir Color
                        </button>
                    </div>

                    {/* Preview del gradiente */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Vista Previa
                        </label>
                        <div
                            className="w-full h-24 rounded-md border-2 border-gray-300"
                            style={{
                                background: `linear-gradient(${gradientData.direccion}, ${gradientData.colores.map(c => `${c.color} ${c.posicion}%`).join(', ')})`
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Imagen Tab */}
            {activeTab === 'imagen' && (
                <div className="space-y-4">
                    {imageData.cdnUrl || imageData.url ? (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Imagen de Fondo
                                </label>
                                <div className="mb-4">
                                    <img
                                        src={imageData.cdnUrl || imageData.url}
                                        alt="Fondo"
                                        className="w-full h-48 object-cover border border-gray-300 rounded-md"
                                    />
                                </div>
                                <button
                                    onClick={handleImageRemove}
                                    className="w-full px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                                >
                                    <FaTrash className="inline mr-2" />
                                    Eliminar Imagen
                                </button>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Opacidad: {Math.round((imageData.opacidad || 1) * 100)}%
                                </label>
                                <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.01"
                                    value={imageData.opacidad || 1}
                                    onChange={(e) => handleImagePropertyChange('opacidad', parseFloat(e.target.value))}
                                    className="w-full"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Modo de Ajuste
                                </label>
                                <select
                                    value={imageData.modo || 'cover'}
                                    onChange={(e) => handleImagePropertyChange('modo', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="cover">Cubrir (Cover)</option>
                                    <option value="contain">Contener (Contain)</option>
                                    <option value="repeat">Repetir (Repeat)</option>
                                </select>
                            </div>
                        </>
                    ) : (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Subir Imagen de Fondo
                            </label>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                className="hidden"
                            />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                className="w-full px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {uploading ? 'Subiendo...' : (
                                    <>
                                        <FaImage className="inline mr-2" />
                                        Subir Imagen
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BackgroundProperties;
